"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

interface PhaseDef {
  id: string;
  name: string;
  sanskrit: string;
  duration: string;
  houseRel: string;
  nature:
    "Rising Transition" | "Peak Transformation" | "Setting Resolution" | "Dhaiya Minor Panoti";
  mindImpact: string;
  careerImpact: string;
  wealthImpact: string;
  remedies: string[];
}

const PHASES: PhaseDef[] = [
  {
    id: "phase1",
    name: "Phase 1: Rising Phase",
    sanskrit: "आद्य चरण (12th House)",
    duration: "2.5 Years (1st Quarter)",
    houseRel: "12th House counted from Natal Moon",
    nature: "Rising Transition",
    mindImpact:
      "Increased mental restlessness, vivid dreams, subconscious anxiety, and sleep pattern shifts.",
    careerImpact:
      "Foreign connections activate; feeling unacknowledged at workplace despite putting in solid hours.",
    wealthImpact:
      "Higher unexpected expenditure on health, travel, or family commitments; need for disciplined budgeting.",
    remedies: [
      "Light a mustard oil lamp under a Peepal tree on Saturday evenings",
      "Chant 'Om Sham Shanaischaraya Namah' 108 times at twilight",
      "Feed birds and crows daily in the morning",
    ],
  },
  {
    id: "phase2",
    name: "Phase 2: Peak Phase",
    sanskrit: "मध्य चरण (1st House / Moon)",
    duration: "2.5 Years (2nd Quarter)",
    houseRel: "Saturn transits directly over Natal Moon",
    nature: "Peak Transformation",
    mindImpact:
      "Deep psychological testing, ego shedding, and building enduring emotional resilience.",
    careerImpact:
      "Heavy work responsibilities; complete professional restructuring leading to lasting authority.",
    wealthImpact:
      "Conservative financial growth; rewards arrive slowly but stay permanently established.",
    remedies: [
      "Recite Hanuman Chalisa daily, especially on Tuesdays and Saturdays",
      "Perform selfless seva or donate food to laborers and the elderly",
      "Avoid starting speculative gambling or unethical shortcuts",
    ],
  },
  {
    id: "phase3",
    name: "Phase 3: Setting Phase",
    sanskrit: "अंत्य चरण (2nd House)",
    duration: "2.5 Years (3rd Quarter)",
    houseRel: "2nd House counted from Natal Moon",
    nature: "Setting Resolution",
    mindImpact:
      "Mental clarity returns; emotional peace and relief from previous peak-phase pressures.",
    careerImpact:
      "Fruit of 5 years of hard work begins to materialize; promotions and stable recognition arrive.",
    wealthImpact:
      "Recovery of financial savings; investments begin yielding disciplined, stable returns.",
    remedies: [
      "Donate black sesame (til) and iron utensils on Saturdays",
      "Offer water to Shivling on Monday and Saturday mornings",
      "Maintain respectful and truthful speech within the family",
    ],
  },
  {
    id: "kantaka",
    name: "Kantaka Shani (4th Dhaiya)",
    sanskrit: "कंटक शनि (4th House)",
    duration: "2.5 Years (Minor Panoti)",
    houseRel: "4th House from Moon (Chest / Home)",
    nature: "Dhaiya Minor Panoti",
    mindImpact:
      "Domestic disputes or temporary lack of happiness at home; mother requires healthcare support.",
    careerImpact:
      "Work relocations, changes of office desk, or temporary professional distance from hometown.",
    wealthImpact:
      "Caution required when purchasing real estate or executing vehicular transactions.",
    remedies: [
      "Recite Shani Chalisa every Saturday evening after sunset",
      "Offer sweet milk to the roots of a Banyan or Peepal tree",
      "Avoid disputes with domestic help and service workers",
    ],
  },
  {
    id: "ashtama",
    name: "Ashtama Shani (8th Dhaiya)",
    sanskrit: "अष्टम शनि (8th House)",
    duration: "2.5 Years (Minor Panoti)",
    houseRel: "8th House from Moon (Transformation)",
    nature: "Dhaiya Minor Panoti",
    mindImpact:
      "Occasional fear of unknown events; intense pull toward spirituality, occult, and meditation.",
    careerImpact:
      "Sudden unexpected shifts in career domain; requires extreme honesty and avoidance of shortcuts.",
    wealthImpact:
      "Delay in receiving ancestral inheritance; avoid taking unsecured high-interest loans.",
    remedies: [
      "Chant Maha Mrityunjaya Mantra (108 times daily)",
      "Recite Dasharatha Shani Stotram on Saturdays",
      "Donate footwear (leather/rubber shoes) to needy workers",
    ],
  },
];

export default function SadeSatiTimelineStudio() {
  const [selectedId, setSelectedId] = useState<string>("phase2");

  const phase = useMemo<PhaseDef>(() => {
    return PHASES.find((p) => p.id === selectedId) || PHASES[1];
  }, [selectedId]);

  const handleConsult = () => {
    const prompt = `Please check if my Janam Kundali is currently running ${phase.name} (${phase.sanskrit}). What are the exact transit dates and authentic Shani remedies for my chart?`;
    try {
      safeSessionSet("seo_entry_intent", "sade_sati_lookup");
      safeSessionSet("seo_entry_path", "/sade-sati");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <PhaseSelector selectedId={selectedId} onSelect={setSelectedId} />
      <PhaseDetailCard phase={phase} onConsult={handleConsult} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Shani Transit Timeline
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        7.5-Year Shani Sade Sati &amp; Dhaiya Calculator (शनि साढ़े साती)
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Check your current Sade Sati phase (Rising, Peak, Setting) and Dhaiya transits to understand
        life impacts and authentic remedies.
      </p>
    </div>
  );
}

function PhaseSelector({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {PHASES.map((p) => {
          const isSelected = p.id === selectedId;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="block text-caption font-bold text-intent-brand">
                {p.duration.split(" ")[0]} {p.duration.split(" ")[1]}
              </span>
              <span className="block text-body-small font-bold text-text-primary">
                {p.name.split(" ")[0]}
              </span>
              <span className="block text-caption text-text-muted truncate">
                {p.sanskrit.split(" ")[0]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PhaseDetailCard({ phase, onConsult }: { phase: PhaseDef; onConsult: () => void }) {
  const chatHref = buildSEOAgentHref(getSEOIntentConfigForSource("/sade-sati"), "/sade-sati", "en");

  const badgeColor =
    phase.nature === "Peak Transformation"
      ? "bg-warning/10 text-warning"
      : phase.nature === "Setting Resolution"
        ? "bg-success/10 text-success"
        : "bg-intent-brand/10 text-intent-brand";

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              🪐 {phase.name} ({phase.sanskrit})
            </h3>
            <span className={`px-3 py-0.5 rounded-full text-caption font-bold ${badgeColor}`}>
              {phase.nature}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Astronomical Position: <strong className="text-text-primary">{phase.houseRel}</strong>{" "}
            &middot; Duration:{" "}
            <span className="font-semibold text-text-primary">{phase.duration}</span>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Check My Current Phase ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            Mind &amp; Emotions
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{phase.mindImpact}</p>
        </div>

        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            Career &amp; Status
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{phase.careerImpact}</p>
        </div>

        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-1">
            Wealth &amp; Finances
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{phase.wealthImpact}</p>
        </div>
      </div>

      <div className="mt-4 p-4 rounded-xl bg-intent-brand/5 border border-intent-brand/20">
        <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-2">
          Prescribed Shani Upay &amp; Remedial Actions
        </p>
        <ul className="space-y-1 text-caption text-text-primary">
          {phase.remedies.map((rem, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <span className="text-intent-brand">✓</span>
              <span>{rem}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Know your Moon sign to calculate exact Sade Sati entry and exit dates.
        </span>
        <Link
          href="/sade-sati/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Open Free Sade Sati Calculator →
        </Link>
      </div>
    </div>
  );
}
